import { type FC, type ReactNode } from 'react'
import { X, Download, FileText, Loader2, AlertCircle } from 'lucide-react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
    type Transaction,
    useDownloadReceipt,
    useTransaction,
} from '@/hooks/useTransactions'
import {
    BTCIcon,
    DOGEIcon,
    ETHIcon,
    NGNIcon,
    TRONIcon,
    USDCIcon,
    USDTIcon,
} from '@/components/rates/CryptoIcons'
import { toast } from 'sonner'

interface TransactionDetailsModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    transaction: Transaction | null
}

const COIN_ICONS: Record<string, FC> = {
    BTC: BTCIcon,
    DOGE: DOGEIcon,
    ETH: ETHIcon,
    TRX: TRONIcon,
    USDC: USDCIcon,
    USDT: USDTIcon,
    NGN: NGNIcon,
}

const STATUS_STYLES: Record<string, { label: string; className: string }> = {
    COMPLETED: {
        label: 'Completed',
        className: 'bg-green-100 text-green-700 hover:bg-green-100',
    },
    SUCCESS: {
        label: 'Completed',
        className: 'bg-green-100 text-green-700 hover:bg-green-100',
    },
    PENDING: {
        label: 'Pending',
        className: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100',
    },
    CONFIRM: {
        label: 'Pending',
        className: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100',
    },
    FAILED: {
        label: 'Failed',
        className: 'bg-red-100 text-red-700 hover:bg-red-100',
    },
    CANCELLED: {
        label: 'Cancelled',
        className: 'bg-gray-100 text-gray-700 hover:bg-gray-100',
    },
}

const titleCase = (value?: string | null) =>
    value
        ? value
              .toLowerCase()
              .split('_')
              .map(w => w.charAt(0).toUpperCase() + w.slice(1))
              .join(' ')
        : '—'

const formatDate = (value?: string | Date | null) =>
    value
        ? new Date(value).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
          })
        : '—'

/** Hide empty, zero and placeholder values rather than printing "0 USDT". */
const hasValue = (value?: string | null) =>
    !!value && value !== 'N/A' && Number(value) !== 0

const Coin: FC<{ code?: string | null; suffix?: string }> = ({
    code,
    suffix,
}) => {
    const symbol = code?.toUpperCase() ?? ''
    const Icon = COIN_ICONS[symbol]
    return (
        <div className="flex items-center gap-2">
            {Icon && (
                <div className="h-5 w-5">
                    <Icon />
                </div>
            )}
            <span className="text-sm font-medium">
                {symbol || '—'}
                {suffix ? ` ${suffix}` : ''}
            </span>
        </div>
    )
}

const Row: FC<{ label: string; children: ReactNode }> = ({
    label,
    children,
}) => (
    <div className="flex items-start justify-between gap-4">
        <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
        <div className="min-w-0 text-right">{children}</div>
    </div>
)

const Value: FC<{ children: ReactNode; mono?: boolean }> = ({
    children,
    mono,
}) => (
    <span
        className={`break-all text-sm font-medium ${mono ? 'font-mono' : ''}`}
    >
        {children}
    </span>
)

export const TransactionDetailsModal: FC<TransactionDetailsModalProps> = ({
    open,
    onOpenChange,
    transaction,
}) => {
    const downloadReceipt = useDownloadReceipt()
    const {
        data: detailResponse,
        isLoading,
        error,
        refetch,
    } = useTransaction(open && transaction ? transaction.id : '')

    const handleDownloadReceipt = () => {
        if (!transaction) return
        downloadReceipt.mutate(transaction.id, {
            onSuccess: () => toast.success('Receipt downloaded successfully'),
            onError: () => toast.error('Failed to download receipt'),
        })
    }

    if (!transaction) return null

    const detail = detailResponse?.data
    const status = STATUS_STYLES[detail?.status ?? ''] ?? {
        label: titleCase(detail?.status),
        className: 'bg-gray-100 text-gray-700 hover:bg-gray-100',
    }
    const isSwap = detail?.transactionContext === 'SWAP'
    const isWithdrawal = detail?.transactionContext === 'WITHDRAWAL'
    const isCredit = detail?.transactionType === 'CREDIT'
    const feeCurrency = detail?.feeCurrency ?? detail?.currency ?? ''

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                showCloseButton={false}
                className="fixed left-1/2 top-1/2 z-50 h-auto max-h-[90vh] w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-hidden border-0 p-0 shadow-2xl data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:left-auto sm:right-6 sm:top-6 sm:bottom-6 sm:translate-x-0 sm:translate-y-0 sm:h-auto sm:max-h-[calc(100%-3rem)] sm:w-[520px] sm:max-w-none sm:rounded-2xl"
            >
                <div className="flex h-full flex-col overflow-hidden bg-background">
                    {/* Header */}
                    <div className="flex items-center justify-between border-b px-6 py-4">
                        <h2 className="text-lg font-semibold">
                            Transaction Details
                        </h2>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onOpenChange(false)}
                            className="h-8 w-8"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6">
                        {isLoading ? (
                            <div className="space-y-4">
                                <Skeleton className="h-14 w-2/3" />
                                {Array.from({ length: 8 }).map((_, i) => (
                                    <Skeleton key={i} className="h-6 w-full" />
                                ))}
                            </div>
                        ) : error || !detail ? (
                            <div className="flex h-[300px] flex-col items-center justify-center gap-4 text-center">
                                <div className="rounded-full bg-red-100 p-3">
                                    <AlertCircle className="h-6 w-6 text-red-600" />
                                </div>
                                <p className="text-sm text-muted-foreground">
                                    Could not load this transaction.
                                </p>
                                <Button
                                    variant="outline"
                                    onClick={() => refetch()}
                                >
                                    Retry
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {/* Amount Section */}
                                <div className="flex items-start gap-4">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#5FA8B4]/10">
                                        <FileText className="h-6 w-6 text-[#5FA8B4]" />
                                    </div>
                                    <div>
                                        <div className="text-2xl font-bold">
                                            {detail.cryptoAmount ?? '—'}{' '}
                                            <span className="text-muted-foreground">
                                                {detail.currency}
                                            </span>
                                        </div>
                                        <div className="text-sm text-muted-foreground">
                                            {isSwap
                                                ? 'Amount swapped'
                                                : isCredit
                                                  ? 'Amount received'
                                                  : 'Amount sent'}
                                        </div>
                                    </div>
                                </div>

                                {/* Transaction Details */}
                                <div className="space-y-4">
                                    <h3 className="text-sm font-semibold uppercase text-muted-foreground">
                                        TRANSACTION DETAILS
                                    </h3>

                                    <div className="space-y-4">
                                        <Row label="Transaction">
                                            <Badge
                                                variant="secondary"
                                                className="bg-[#5FA8B4]/10 text-[#5FA8B4] hover:bg-[#5FA8B4]/10"
                                            >
                                                {titleCase(
                                                    detail.transactionContext
                                                )}{' '}
                                                ({isCredit ? 'Credit' : 'Debit'}
                                                )
                                            </Badge>
                                        </Row>

                                        <Row label="Status">
                                            <Badge
                                                variant="secondary"
                                                className={status.className}
                                            >
                                                {status.label}
                                            </Badge>
                                        </Row>

                                        <Row label="Account name">
                                            <Value>{detail.accountName}</Value>
                                        </Row>

                                        {isSwap ? (
                                            <>
                                                <Row label="From">
                                                    <Coin
                                                        code={
                                                            detail.fromCurrency ??
                                                            detail.currency
                                                        }
                                                        suffix={
                                                            detail.cryptoAmount ??
                                                            undefined
                                                        }
                                                    />
                                                </Row>
                                                <Row label="To">
                                                    <Coin
                                                        code={
                                                            detail.received
                                                                ?.currency ??
                                                            detail.toCurrency
                                                        }
                                                        suffix={
                                                            detail.received
                                                                ?.amount ??
                                                            (detail.status ===
                                                            'COMPLETED'
                                                                ? undefined
                                                                : '(not received)')
                                                        }
                                                    />
                                                </Row>
                                                {hasValue(
                                                    detail.executionPrice
                                                ) && (
                                                    <Row label="Rate">
                                                        <Value>
                                                            {
                                                                detail.executionPrice
                                                            }
                                                        </Value>
                                                    </Row>
                                                )}
                                            </>
                                        ) : (
                                            <Row
                                                label={
                                                    isCredit
                                                        ? 'Wallet'
                                                        : 'Originating wallet'
                                                }
                                            >
                                                <Coin code={detail.currency} />
                                            </Row>
                                        )}

                                        {hasValue(detail.fiatAmount) && (
                                            <Row label="Fiat amount">
                                                <Value>
                                                    ₦{detail.fiatAmount}
                                                </Value>
                                            </Row>
                                        )}

                                        {hasValue(detail.platformFee) && (
                                            <Row label="Platform fee">
                                                <Value>
                                                    {detail.platformFee}{' '}
                                                    {feeCurrency}
                                                </Value>
                                            </Row>
                                        )}

                                        {hasValue(detail.networkFee) && (
                                            <Row label="Network fee">
                                                <Value>
                                                    {detail.networkFee}{' '}
                                                    {detail.currency}
                                                </Value>
                                            </Row>
                                        )}

                                        {hasValue(detail.totalAmount) && (
                                            <Row label="Total deducted">
                                                <Value>
                                                    {detail.totalAmount}{' '}
                                                    {detail.currency}
                                                </Value>
                                            </Row>
                                        )}

                                        {detail.network &&
                                            detail.network !== 'N/A' && (
                                                <Row label="Network">
                                                    <Value>
                                                        {detail.network.toUpperCase()}
                                                    </Value>
                                                </Row>
                                            )}

                                        {detail.walletAddress &&
                                            detail.walletAddress !== 'N/A' && (
                                                <Row label="Wallet address">
                                                    <Value mono>
                                                        {detail.walletAddress}
                                                    </Value>
                                                </Row>
                                            )}

                                        <Row label="Transaction ID">
                                            <Value mono>
                                                #{detail.transactionId}
                                            </Value>
                                        </Row>

                                        {detail.transactionUniqueId && (
                                            <Row label="Reference">
                                                <Value mono>
                                                    {detail.transactionUniqueId}
                                                </Value>
                                            </Row>
                                        )}

                                        <Row label="Created">
                                            <Value>
                                                {formatDate(detail.date)}
                                            </Value>
                                        </Row>

                                        {detail.executedAt && (
                                            <Row label="Executed">
                                                <Value>
                                                    {formatDate(
                                                        detail.executedAt
                                                    )}
                                                </Value>
                                            </Row>
                                        )}

                                        {detail.description && (
                                            <Row label="Note">
                                                <Value>
                                                    {detail.description}
                                                </Value>
                                            </Row>
                                        )}
                                    </div>
                                </div>

                                {/* Destination Details: only withdrawals send to an address */}
                                {isWithdrawal && (
                                    <div className="space-y-4">
                                        <h3 className="text-sm font-semibold uppercase text-muted-foreground">
                                            DESTINATION DETAILS
                                        </h3>

                                        <div className="space-y-4">
                                            <Row label="Destination wallet">
                                                <Value>
                                                    {detail.destinationWallet ??
                                                        '—'}
                                                </Value>
                                            </Row>
                                            <Row label="Destination address">
                                                <Value mono>
                                                    {detail.destinationAddress ??
                                                        '—'}
                                                </Value>
                                            </Row>
                                            {detail.destinationTag && (
                                                <Row label="Destination tag">
                                                    <Value mono>
                                                        {detail.destinationTag}
                                                    </Value>
                                                </Row>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="border-t p-6">
                        <Button
                            className="w-full gap-2 bg-[#5FA8B4] hover:bg-[#5FA8B4]/90"
                            size="lg"
                            onClick={handleDownloadReceipt}
                            disabled={downloadReceipt.isPending}
                        >
                            {downloadReceipt.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Download className="h-4 w-4" />
                            )}
                            {downloadReceipt.isPending
                                ? 'Downloading...'
                                : 'Download receipt'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
